import { USER_ROLES } from "@nlc/shared-types";
import { Sparkles } from "lucide-react";
import { useState } from "react";

import type { UserRole } from "@/features/auth/permissions/config";

import { Button, ButtonGroup } from "@/components/button";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItemDescription,
  MenuItemLabel,
  MenuPopover,
  MenuTrigger,
} from "@/components/menu";
import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess";

import { useAlphaEditModelsQuery } from "./hooks/useAlphaEditModelsQuery";

type GenerateButtonProps = {
  changeCount: number;
  isDisabled: boolean;
  isLoading: boolean;
  onGenerate: (model?: string) => void;
};

function applyLabel(changeCount: number) {
  if (changeCount === 0) return "Apply changes";

  return `Apply ${changeCount} ${changeCount === 1 ? "change" : "changes"}`;
}

export function GenerateButton({
  changeCount,
  isDisabled,
  isLoading,
  onGenerate,
}: GenerateButtonProps) {
  const { hasRole } = useRoleAccess();
  const label = applyLabel(changeCount);
  const isSuperAdmin = hasRole([USER_ROLES.SUPER_ADMIN] as UserRole[]);
  const editModels = useAlphaEditModelsQuery(isSuperAdmin).data;
  const [choice, setChoice] = useState<{
    key: string;
    source: typeof editModels;
  }>();

  if (!isSuperAdmin || !editModels) {
    return (
      <Button
        className="w-full"
        isDisabled={isDisabled}
        isLoading={isLoading}
        onPress={() => onGenerate()}
        size="lg"
      >
        <Sparkles size={18} />
        {label}
      </Button>
    );
  }

  const selectedKey =
    choice?.source === editModels ? choice.key : editModels.defaultKey;
  const selected = editModels.models.find(({ key }) => key === selectedKey);

  return (
    <ButtonGroup
      className="w-full"
      title={`Generate with ${selected?.label ?? selectedKey}`}
    >
      <Button
        className="flex-1"
        isDisabled={isDisabled}
        isLoading={isLoading}
        onPress={() => onGenerate(selectedKey)}
        size="lg"
      >
        <Sparkles size={18} />
        {label}
      </Button>
      <MenuTrigger>
        <MenuButton
          aria-label="Choose edit model"
          isDisabled={isDisabled || isLoading}
          isIconOnly
          size="lg"
          variant="primary"
        />
        <MenuPopover placement="bottom end">
          <Menu
            onSelectionChange={(keys) => {
              const [key] = keys instanceof Set ? [...keys] : [];

              if (key) setChoice({ key: String(key), source: editModels });
            }}
            selectedKeys={[selectedKey]}
            selectionMode="single"
          >
            {editModels.models.map(({ key, label, priceLabel }) => (
              <MenuItem id={key} key={key}>
                <MenuItemLabel>{label}</MenuItemLabel>
                <MenuItemDescription>{priceLabel}</MenuItemDescription>
              </MenuItem>
            ))}
          </Menu>
        </MenuPopover>
      </MenuTrigger>
    </ButtonGroup>
  );
}
